import { useQuery } from '@apollo/client';
import { useDato, useMarket } from '@polestar/shared-front-myorder';
import { GET_TRANSLATIONS } from '../apollo/queries/GetDatoContent';

const DEFAULT_MARKET = 'en_GB';

type KeyValProps = {
  key: string;
  value: string;
};

type DatoContentDataProps = {
  [key: string]: KeyValProps[];
};

// Helper function to split array into chunks of specified size
const chunkArray = <T>(array: readonly T[], chunkSize: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

const loggedinKeys = ['Side Navigation Dropdown'] as const;
const sharedKeys = ['Cancel', 'Something went wrong', 'VIN', 'Error'] as const;
const extrasKeys = [
  'Order.ServiceBooked',
  'Webshop.TotalPrice',
  'Webshop.TotalVat',
  'CheckoutView.Delivery.LoweringKitTitle',
  'CheckoutView.Delivery.DeliveryMethodTitle',
  'Webshop.Title',
  'OrderDetailsView.Receipt.DownloadError',
  'OrderDetailsView.Receipt.DownloadButton'
] as const;
const orderListKeys = [
  'Orders.Title',
  'Orderlist.NoActiveOrder',
  'NoOrders.Title',
  'NoOrders.SubTitle',
  'Orderlist.PreConfig.Url',
  'Orderlist.PreConfig.Label',
  'Orderlist.Configure.Url',
  'Orderlist.Configure.Label',
  'OrderItem.Details.OrderNumber',
  'OrderItem.Details.OrderNumberWith#',
  'OrderItem.Details.OrderDate',
  'OrderItem.Details.LatestSaved',
  'OrderItem.Details.Status',
  'OrderItem.Details.Price',
  'OrderItem.Details.MonthlyPrice',
  'OrderItem.Details.Retailer',
  'OrderItem.Details.DeliveryMethod',
  'OrderItem.Details.EstimatedDeliveryDate',
  'OrderItem.Details.HandoverDate',
  'OrderItem.Details.Interior',
  'OrderItem.Details.Exterior',
  'OrderItem.Details.Wheels',
  'OrderItem.Details.Title',
  'OrderItem.Details.DeliveryDate',
  'OrderItem.Details.DeliveryLocation',
  'OrderItem.Details.Vin',
  'OrderItem.Details.Model',
  'OrderItem.Details.Version',
  'OrderItem.Details.Packages',
  'OrderItem.Details.Winterwheels',
  'OrderItem.Details.Towbar',
  'OrderItem.Details.LoweringKit',
  'OrderItem.Details.Charging',
  'OrderItem.Action.Continue',
  'OrderItem.Action.View',
  'OrderItem.Action.Remove',
  'OrderItem.Extra.Title',
  'OrderItem.Configurations.OrderAmountStatus.Single',
  'OrderItem.Configurations.OrderAmountStatus.Multiple',
  'OrderItem.Configurations.RemoveAll',
  'OrderType.Processing.Delivery',
  'OrderType.Delivered',
  'OrderType.Configurations',
  'OrderItem.OrderLine.Extra.Status',
  'OrderItem.ConfirmDialog.Remove.Title',
  'OrderItem.ConfirmDialog.RemoveAll.Title',
  'OrderItem.ConfirmDialog.RemoveAll.Message',
  'Delivery.NeedsMoreInformation',
  'Delivery.OrderStatus.NeedsMoreInformation',
  'OrderDetailsView.Receipt.DownloadError',
  'OrderDetailsView.Receipt.DownloadButton',
  'Order.ServiceBooked',
  'DeliveryMethods.OverTheAir',
  'DeliveryMethods.HomeDelivery',
  'DeliveryMethods.Collect',
  'DeliveryMethods.Personalized',
  'DeliveryMethods.AtHandover',
  'DeliveryMethods.TyreHotel',
  'DeliveryMethods.InstallationAtWorkshop',
  'DeliveryMethods.PaidHomeDelivery',
  'FinancingOption.Remarketing.Loan',
  'FinancingOptions.Cash',
  'FinancingOptions.FinancialLeasing',
  'FinancingOptions.Leasing',
  'FinancingOptions.Loan',
  'FinancingOptions.LoanWithBalloon',
  'FinancingOptions.LoanWithBalloonGFV',
  'FinancingOptions.OperationalLeasing',
  'FinancingOptions.PersonalLeasing',
  'FinancingOptions.PersonalLoan',
  'FinancingOptions.Subscription',
  'OrderItem.Status.None',
  'OrderItem.Status.Submitted',
  'OrderItem.Status.Confirmed',
  'OrderItem.Status.Planned',
  'OrderItem.Status.Delivered',
  'OrderItem.Status.Returned',
  'OrderItem.Status.PendingReturn',
  'OrderItem.Status.PartiallyDelivered',
  'OrderItem.Status.Returned',
  'OrderItem.Status.Ordered',
  'OrderItem.Status.ReadyForDelivery',
  'OrderItem.Status.Expired'
] as const;

const orderListKeysChunks = chunkArray(orderListKeys, 20);
const [orderListKeys1, orderListKeys2, orderListKeys3, orderListKeys4] = orderListKeysChunks;

export type TranslationKey =
  | (typeof sharedKeys)[number]
  | (typeof extrasKeys)[number]
  | (typeof orderListKeys)[number]
  | (typeof loggedinKeys)[number];

export const useQueryDatoContent = () => {
  const { currentMarket } = useMarket();
  const locale = currentMarket ? currentMarket.cmsLocale : DEFAULT_MARKET;

  const { data, loading } = useQuery<DatoContentDataProps>(GET_TRANSLATIONS, {
    variables: {
      locale,
      sharedDictionaryLookup: sharedKeys,
      extrasDictionaryLookup: extrasKeys,
      orderListDictionaryLookup1: orderListKeys1,
      orderListDictionaryLookup2: orderListKeys2,
      orderListDictionaryLookup3: orderListKeys3,
      orderListDictionaryLookup4: orderListKeys4,
      loggedinDictionaryLookup: loggedinKeys
    },
    context: { dato: true }
  });

  const { addTranslation } = useDato();

  if (data) {
    addTranslation(
      Object.values(data).reduce(
        (result, chunk) =>
          chunk.reduce(
            (map: { [key: string]: string } = {}, obj: { key: string; value: string }) => {
              map[obj.key] = obj.value;
              return map;
            },
            result
          ),
        {}
      )
    );
  }
  return {
    isCompleted: !!data,
    loading
  };
};

export const useTranslation = () => {
  const { text } = useDato();

  return (
    key: TranslationKey,
    options?: { variables?: Record<string, string>; fallback?: string }
  ) => text(key, options);
};
